import { BlueButton } from '../../../../customComponents/buttons/BlueButton'
import { AddCircle, HomeOutlined } from '@mui/icons-material'
import { ThingsToConsiderButton } from '../ThingsToConsiderButton'
import { Box, CircularProgress, Stack, Typography } from '@mui/material'
import type { ThingsToConsiderSection } from '../ThingsToConsiderCard'
import {
  useCetaClassroomsAndFocusStudents,
  useCetaClassroomsAndFocusStudentsModal
} from '../../../../../hooks/use-cetaClassroomsAndFocusStudents'
import { useEffect, useState } from 'react'
import { type CETAClassroomAndFocusStudents } from '../../../../../models/CETAClassroomAndFocusStudents'
import { BasicTable, type BasicTableColumn } from '../BasicTable'
import { DeleteCetaClassroomAndFocusStudents, GetEEMIdByOrgId, UpdateCetaClassroomAndFocusStudents } from '../../../../../services/AnnualPlanService'
import { toast } from 'react-toastify'
import { CetaClassroomsAndFocusStudentsModal, type UpdateCetaClassroomAndFocusStudentsFormValues } from './CetaClassroomsandFocusStudentsModal'
import { BannerModal } from '../../../../customComponents/BannerModal'
import { type SubBasicTableColumn, SubBasicTable } from '../SubBasicTable'
import { isValidPositiveNumber } from '../../../../../utils/isValidNumber'
import { CETAClassroomAndFocusStudentTrackingModal } from './CetaClassroomAndFocusStudentTrackingModal'
import { FocusStudentSubmittedCheckbox } from './FocusStudentSubmittedCheckbox'

interface Props {
  priorityId: number
  planOrgId: number
  isdOrgId: number
  readOnly: boolean
  trackingReadonly: boolean
  showTableOnly?: boolean
  hideExpand?: boolean
  implementationPhase?: boolean
  isTracking?: boolean
  budgetOverviewView?: boolean
}

type CetaClassroomAndFocusStudentsExpandedRow = CETAClassroomAndFocusStudents & {
  classroom: string
  classroomId?: number
  teacherName: string
  gradeLevels: string
  rcnContactName: string
  rcnContactEmail: string
  focusStudents: string
  action: string
  completed: string
  completedInStart: string
  cetaKickoffCompletedDate: string
  focusStudentDataSubmitted: JSX.Element
  budgetSpentString: string
  trackingAction: string
}

export const CetaClassroomsAndFocusStudentsList = (props: Props): JSX.Element => {
  const { cetaClassroomsAndFocusStudents, isLoading, reloadCetaClassroomsAndFocusStudents } = useCetaClassroomsAndFocusStudents(props.priorityId, props.implementationPhase)
  const [cetaClassroomAndFocusStudentsToDelete, setCetaClassroomAndFocusStudentsToDelete] = useState<CETAClassroomAndFocusStudents | null>(null)
  const {
    handleAddCetaClassroomAndFocusStudents,
    handleEditCetaClassroomAndFocusStudents,
    handleCloseModal: closeModal,
    initialValues,
    handleTrackClassroomAndFocusStudent,
    handleCloseTrackingModal,
    classroomAndFocusStudentToTrack,
    modalState
  } = useCetaClassroomsAndFocusStudentsModal()
  const [editClassroomNumber, setEditClassroomNumber] = useState<number | undefined>(undefined)
  const [classroomAndFocusStudentWithAdditionalDetailsToDelete, setClassroomAndFocusStudentWithAdditionalDetailsToDelete] = useState<CetaClassroomAndFocusStudentsExpandedRow | null>(null)
  const [classroomAndFocusStudentWithTrackingToDelete, setClassroomAndFocusStudentWithTrackingToDelete] = useState<CetaClassroomAndFocusStudentsExpandedRow | null>(null)
  const [eemId, setEemId] = useState<number | null>(null)

  useEffect(() => {
    if (modalState === 'none') {
      setEditClassroomNumber(undefined)
    }
  }, [modalState])

  useEffect(() => {
    // get eem id from isdOrgid
    const fetchData = async (): Promise<void> => {
      const eemId: number | null = await GetEEMIdByOrgId(props.isdOrgId)
      setEemId(eemId)
    }

    void fetchData()
  }, [props.isdOrgId])

  const handleDeleteCetaClassroomAndFocusStudents = async (cetaClassroomAndFocusStudents: CETAClassroomAndFocusStudents): Promise<void> => {
    const { success, errors } = await DeleteCetaClassroomAndFocusStudents(cetaClassroomAndFocusStudents.id, cetaClassroomAndFocusStudents.isUpdate)

    if (!success) {
      errors.forEach(e => toast.error(e))
      return
    }

    await reloadCetaClassroomsAndFocusStudents()
    toast.success('CETA Classroom and Focus Students Deleted')
    setCetaClassroomAndFocusStudentsToDelete(null)
  }

  const handleCloseModal = (): void => {
    setEditClassroomNumber(undefined)
    void closeModal()
  }
  const getUpdatedClassroomsAndFocusStudents = (cetaClassroomAndFocusStudents: CETAClassroomAndFocusStudents): UpdateCetaClassroomAndFocusStudentsFormValues => {
    return {
      id: cetaClassroomAndFocusStudents.id,
      priorityId: cetaClassroomAndFocusStudents.priorityId,
      organizationType: cetaClassroomAndFocusStudents.organizationType,
      organizationId: cetaClassroomAndFocusStudents.organization.id,
      buildingId: cetaClassroomAndFocusStudents.building.id,
      numberOfClassrooms: cetaClassroomAndFocusStudents.numberOfClassrooms,
      numberOfFocusStudents: cetaClassroomAndFocusStudents.numberOfFocusStudents,
      budgetRequest: cetaClassroomAndFocusStudents.budgetRequest,
      budgetRequestReason: cetaClassroomAndFocusStudents.budgetRequestReason,
      classrooms: cetaClassroomAndFocusStudents.classrooms?.map(classroom => ({
        id: classroom.id,
        teacherName: classroom.teacherName,
        gradeLevels: classroom.gradeLevels,
        rcnContactName: classroom.rcnContactName,
        rcnContactEmail: classroom.rcnContactEmail,
        cetaCompletedInStart: classroom.cetaCompletedInStart,
        cetaKickoffCompletedDate: classroom.cetaKickoffCompletedDate != null ? classroom.cetaKickoffCompletedDate : null,
        budgetSpent: isValidPositiveNumber(classroom.budgetSpent) ? Number(classroom.budgetSpent) : null,
        focusStudents: classroom.focusStudents.map(fs => ({
          id: fs.id,
          initials: fs.initials,
          boyDataSubmitted: fs.boyDataSubmitted,
          eoyDataSubmitted: fs.eoyDataSubmitted
        }))
      })),
      isUpdate: true,
      isImplementationPhase: props.implementationPhase
    }
  }

  const handleDeleteAdditionalDetails = async (cetaClassroomAndFocusStudents: CetaClassroomAndFocusStudentsExpandedRow): Promise<void> => {
    const updatedClassroomsAndFocusStudents = getUpdatedClassroomsAndFocusStudents(cetaClassroomAndFocusStudents)
    updatedClassroomsAndFocusStudents.classrooms = updatedClassroomsAndFocusStudents.classrooms?.filter(c => c.id !== cetaClassroomAndFocusStudents.classroomId)

    const { success, errors } = await UpdateCetaClassroomAndFocusStudents(updatedClassroomsAndFocusStudents)

    if (!success) {
      errors.forEach(e => toast.error(e))
      return
    }

    await reloadCetaClassroomsAndFocusStudents()
    toast.success('CETA Classroom and Focus Students Updated')
    setClassroomAndFocusStudentWithAdditionalDetailsToDelete(null)
  }

  const handleDeleteTrackingDetails = async (cetaClassroomAndFocusStudents: CetaClassroomAndFocusStudentsExpandedRow): Promise<void> => {
    const updatedClassroomsAndFocusStudents = getUpdatedClassroomsAndFocusStudents(cetaClassroomAndFocusStudents)
    updatedClassroomsAndFocusStudents.classrooms = updatedClassroomsAndFocusStudents.classrooms?.map(c =>
      c.id === cetaClassroomAndFocusStudents.classroomId
        ? {
            ...c,
            cetaCompletedInStart: null,
            cetaKickoffCompletedDate: null,
            budgetSpent: null,
            focusStudents: c.focusStudents.map(fs => ({
              ...fs,
              boyDataSubmitted: null,
              eoyDataSubmitted: null
            }))
          }
        : c
    )

    const { success, errors } = await UpdateCetaClassroomAndFocusStudents(updatedClassroomsAndFocusStudents)

    if (!success) {
      errors.forEach(e => toast.error(e))
      return
    }

    await reloadCetaClassroomsAndFocusStudents()
    toast.success('CETA Classroom and Focus Students Updated')
    setClassroomAndFocusStudentWithTrackingToDelete(null)
  }

  const thingsToConsiderSections: ThingsToConsiderSection[] = [
    {
      title: 'Developing a Classroom Team',
      items: [
        <>
          A coach may work with an administrator and a building team to identify classrooms as candidates for completing the CETA. Considerations for selection may include:
          <ul>
            <li>A classroom with a new teacher</li>
            <li>A classroom with one or more students with higher support needs</li>
            <li>A motivated teacher interested in further developing classroom practices</li>
          </ul>
        </>
      ]
    }
  ]

  const expandedContent = (cetaClassroomAndFocusStudent: CETAClassroomAndFocusStudents): JSX.Element | null => {
    const subColumns: SubBasicTableColumn[] = [
      { id: 'classroom', label: 'Classroom', minWidth: 150 },
      { id: 'teacherName', label: 'Teacher Name', minWidth: 150 },
      { id: 'gradeLevels', label: 'Grade Levels', minWidth: 100 },
      { id: 'rcnContactName', label: 'RCN Contact', minWidth: 150 },
      { id: 'rcnContactEmail', label: 'RCN Contact', minWidth: 200 },
      { id: 'focusStudents', label: 'Focus Students', minWidth: 150 },
      { id: 'action', label: '' },
      { id: 'completed', label: 'Completed', isTrackingColumn: true },
      { id: 'completedInStart', label: 'In PDS', isTrackingColumn: true },
      { id: 'cetaKickoffCompletedDate', label: 'Completion Date', isTrackingColumn: true },
      { id: 'focusStudentDataSubmitted', label: 'Focus Student Data Submitted', isTrackingColumn: true },
      { id: 'budgetSpentString', label: 'Spent', isTrackingColumn: true },
      { id: 'trackingAction', label: '', isTrackingColumn: true }
    ]
    const subRows: CetaClassroomAndFocusStudentsExpandedRow[] = [
      ...cetaClassroomAndFocusStudent.classrooms?.map((classroom, index) => ({
        ...cetaClassroomAndFocusStudent, // pass the entire object for editing
        id: cetaClassroomAndFocusStudent.id,
        classroom: `Classroom ${index + 1}`,
        classroomId: classroom.id,
        teacherName: classroom.teacherName,
        gradeLevels: classroom.gradeLevels,
        rcnContactName: classroom.rcnContactName,
        rcnContactEmail: classroom.rcnContactEmail,
        focusStudents: classroom.focusStudents.map(fs => fs.initials).join(', '),
        action: '',
        completed: classroom.cetaCompletedInStart != null && classroom.cetaKickoffCompletedDate != null
          ? 'Yes'
          : classroom.cetaCompletedInStart == null || classroom.cetaKickoffCompletedDate == null
            ? ''
            : 'No',
        completedInStart: classroom.cetaCompletedInStart === true
          ? 'Yes'
          : classroom.cetaCompletedInStart === false
            ? 'No'
            : '',
        cetaKickoffCompletedDate: classroom.cetaKickoffCompletedDate != null ? new Date(classroom.cetaKickoffCompletedDate).toLocaleDateString() : '',
        focusStudentDataSubmitted: <>
          {classroom.focusStudents.map(fs => (
            <Stack key={fs.id} direction='row' alignItems='center' gap='.5rem' fontSize='12px' border='1px solid var(--blue-500)' borderRadius='1rem' overflow='hidden' pr='.25rem'>
              <Box
                sx={{
                  width: '36px',
                  height: '28px',
                  bgcolor: 'var(--blue-500)',
                  color: 'var(--white)',
                  display: 'grid',
                  placeContent: 'center'
                }}
              >{fs.initials}</Box>
              <FocusStudentSubmittedCheckbox
                text='BOY'
                value={fs.boyDataSubmitted === true}
                dataTestId={`focusStudent-${fs.initials}-boyDataSubmitted`}
              />
              <FocusStudentSubmittedCheckbox
                text='EOY'
                value={fs.eoyDataSubmitted === true}
                dataTestId={`focusStudent-${fs.initials}-eoyDataSubmitted`}
              />
            </Stack>
          ))}
        </>,
        budgetSpentString: isValidPositiveNumber(classroom.budgetSpent) ? '$' + Number(classroom.budgetSpent).toLocaleString() : '',
        trackingAction: ''
      })) ?? []
    ]

    const hasClassroomAdditionalDetails = cetaClassroomAndFocusStudent.classrooms != null && cetaClassroomAndFocusStudent.classrooms.length > 0
    const hasTrackingDetails = cetaClassroomAndFocusStudent.classrooms?.some(c => {
      const hasClassroomLevelTracking = c.cetaCompletedInStart != null || c.cetaKickoffCompletedDate != null
      const hasFocusStudentLevelTracking = c.focusStudents.some(fs => fs.boyDataSubmitted === true || fs.eoyDataSubmitted === true)
      return hasClassroomLevelTracking || hasFocusStudentLevelTracking
    }) ?? false

    const handleEditAdditionalDetails = (classroom: CetaClassroomAndFocusStudentsExpandedRow): void => {
      setEditClassroomNumber(classroom.classroomId)
      void handleEditCetaClassroomAndFocusStudents(cetaClassroomAndFocusStudent)
    }
    const handleEditTrackingDetails = (classroom: CetaClassroomAndFocusStudentsExpandedRow): void => {
      setEditClassroomNumber(classroom.classroomId)
      void handleTrackClassroomAndFocusStudent(cetaClassroomAndFocusStudent)
    }

    if (!hasClassroomAdditionalDetails) return null

    return (
      <Box
        sx={{
          p: '2rem',
          backgroundColor: 'var(--grey-100)'
        }}
      >
        <SubBasicTable
          dataTestId={`focusClassroomsTable-${cetaClassroomAndFocusStudent.id}`}
          columns={subColumns}
          rows={subRows}
          hasTrackingDetails={hasTrackingDetails}
          readonly={props.readOnly}
          trackingReadonly={props.trackingReadonly}
          onEditAdditionalDetails={(classroom) => { handleEditAdditionalDetails(classroom) }}
          onDeleteAdditionalDetails={(classroom) => { setClassroomAndFocusStudentWithAdditionalDetailsToDelete(classroom) }}
          onEditTracking={(classroom) => { handleEditTrackingDetails(classroom) } }
          onDeleteTracking={(classroom) => { setClassroomAndFocusStudentWithTrackingToDelete(classroom) }}
        />
      </Box>
    )
  }

  const columns: BasicTableColumn[] = [
    { id: 'organizationName', label: 'Organization Info', minWidth: 200 },
    { id: 'buildingName', label: 'Building Info', minWidth: 200 },
    { id: 'numberOfClassrooms', label: 'Classrooms', minWidth: 100 },
    { id: 'numberOfFocusStudents', label: 'Focus Students', minWidth: 100 },
    { id: 'budgetRequestReason', label: 'Resources', minWidth: 200 },
    { id: 'budgetRequestString', label: 'Budget', minWidth: 100 }
  ]

  const rows = cetaClassroomsAndFocusStudents.map(c => ({
    ...c,
    organizationName: c.organization.name,
    buildingName: c.building.name,
    budgetRequestString: c.adjustedBudgetRequest != null ? `$${c.adjustedBudgetRequest}` : `$${c.budgetRequest}`,
    expandedContent: expandedContent(c)
  }))

  return <>
    {props.showTableOnly !== true && (
      <Box sx={{ width: '100%', alignItems: 'center' }}>
        {!props.readOnly &&
          <BlueButton onClick={handleAddCetaClassroomAndFocusStudents} dataTestId='addClassroomButton'>
            Add CETA Classrooms & Focus Students <AddCircle sx={{ ml: '10px' }} fontSize='small' />
          </BlueButton>
      }

        <Box sx={{ float: 'right' }}>
          <ThingsToConsiderButton
            buttonDataTestId='cetaClassroomsAndFocusStudentsThingsToConsiderButton'
            thingsToConsiderSections={thingsToConsiderSections}
            cardDataTestId='cetaClassroomsAndFocusStudentsThingsToConsiderCard'
          />
        </Box>
      </Box>
    )}

    <Box
      data-testid='cetaClassroomsAndFocusStudentsList'
      sx={{
        border: '1px solid var(--green-300)',
        backgroundColor: 'var(--white)',
        borderRadius: '8px',
        padding: cetaClassroomsAndFocusStudents.length === 0 ? '24px' : '0',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0px 10px 10px -8px #3B353E4D'
      }}
    >
      {isLoading
        ? <CircularProgress sx={{ alignSelf: 'center' }} />
        : (cetaClassroomsAndFocusStudents.length === 0
            ? <>
              <HomeOutlined sx={{ color: 'var(--green-300)', width: '144px', height: '144px', alignSelf: 'center' }} />

              <Box sx={{ display: 'flex', flexDirection: 'column', textAlign: 'center' }}>
                <Typography variant='h4'>No CETA Classrooms & Focus Students Added</Typography>
                {!props.readOnly &&
                  <Typography variant='body1' fontSize='16px'>
                    Use the Add CETA Classrooms & Focus Students button to add CETA Classrooms & Focus Students to your Annual Plan.
                  </Typography>
                }
              </Box>
            </>
            : <BasicTable
                dataTestId='cetaClassroomsAndFocusStudentsTable'
                columns={columns}
                rows={rows}
                isLoading={isLoading}
                readonly={props.readOnly}
                trackingReadonly={props.trackingReadonly}
                onEdit={handleEditCetaClassroomAndFocusStudents}
                onDelete={setCetaClassroomAndFocusStudentsToDelete}
                hideExpand={props.hideExpand}
                hideDelete={props.isTracking}
                implementationPhase={props.implementationPhase}
                onUpdate={handleEditCetaClassroomAndFocusStudents}
                onTrack={handleTrackClassroomAndFocusStudent}
                budgetOverviewView={props.budgetOverviewView}
            />)
      }

      {(modalState === 'add' || (modalState === 'edit' && initialValues != null)) &&
        <CetaClassroomsAndFocusStudentsModal
          open={modalState === 'add' || (modalState === 'edit' && initialValues != null)}
          onClose={handleCloseModal}
          onSaved={reloadCetaClassroomsAndFocusStudents}
          priorityId={props.priorityId}
          planOrgId={props.planOrgId}
          isdOrgId={props.isdOrgId}
          eemId={eemId}
          initialValues={initialValues}
          implementationPhase={props.implementationPhase}
          editClassroomNumber={editClassroomNumber}
        />
      }

      {modalState === 'track' && classroomAndFocusStudentToTrack != null && (
        <CETAClassroomAndFocusStudentTrackingModal
          open={modalState === 'track' && classroomAndFocusStudentToTrack != null}
          onClose={handleCloseTrackingModal}
          onSaved={reloadCetaClassroomsAndFocusStudents}
          cetaClassroomAndFocusStudents={classroomAndFocusStudentToTrack}
          implementationPhase={props.implementationPhase === true}
          editClassroomNumber={editClassroomNumber}
        />
      )}

      {classroomAndFocusStudentWithAdditionalDetailsToDelete != null && (
        <BannerModal
          title='Are you sure?'
          cancelButtonText='No, Cancel'
          confirmButtonText='Yes, Delete'
          dataTestId='confirmDeleteClassroomAndFocusStudentWithAdditionalDetailsModal'
          bodyContent={'Are you sure you want to delete this item? This will also remove any tracking data associated with it.'}
          // TODO: Use a shared current item for this
          onClose={() => { setClassroomAndFocusStudentWithAdditionalDetailsToDelete(null) }}
          onConfirm={() => { void handleDeleteAdditionalDetails(classroomAndFocusStudentWithAdditionalDetailsToDelete) }}
          maxWidth='xs'
          bottom=''
          position='fixed'
        />
      )}

      {classroomAndFocusStudentWithTrackingToDelete != null && (
        <BannerModal
          title='Are you sure?'
          cancelButtonText='No, Cancel'
          confirmButtonText='Yes, Delete'
          dataTestId='confirmDeleteClassroomAndFocusStudentWithTrackingModal'
          bodyContent={'Are you sure you want to delete this item?'}
          // TODO: Use a shared current item for this
          onClose={() => { setClassroomAndFocusStudentWithTrackingToDelete(null) }}
          onConfirm={() => { void handleDeleteTrackingDetails(classroomAndFocusStudentWithTrackingToDelete) }}
          maxWidth='xs'
          bottom=''
          position='fixed'
        />
      )}

      {cetaClassroomAndFocusStudentsToDelete != null &&
        <BannerModal
          title='Are you sure?'
          cancelButtonText='No, Cancel'
          confirmButtonText='Yes, Delete'
          dataTestId='confirmDeleteCetaClassroomAndFocusStudentsModal'
          bodyContent={'Are you sure you want to delete this item?'}
          onClose={() => { setCetaClassroomAndFocusStudentsToDelete(null) }}
          onConfirm={() => { void handleDeleteCetaClassroomAndFocusStudents(cetaClassroomAndFocusStudentsToDelete) }}
          maxWidth='xs'
          bottom=''
          position='fixed'
        />
      }
    </Box>
  </>
}
